/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

import { ageTypes } from "../util/ageTypes";
import { availableForCommunication } from "../util/availableForCommunication";
import { genderTypes } from "../util/genderTypes";
import { housesitCountries } from "../util/housesitCountries";
import { propertyExperienceTypes } from "../util/propertyExperienceTypes";

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [

  {
    key: 'locationCharacteristics',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'beach', label: 'Beach' },
      { option: 'city', label: 'City or Town' },
      { option: 'countryside', label: 'Countryside' },
      { option: 'mountains', label: 'Mountains' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Product group',
      group: 'primary',
    },
    showConfig: {
      label: 'What best describes the type of scenery near your property',
      isDetail: true,
    },
    saveConfig: {
      label: 'Location characteristics',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'publicTransportAccess',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "yes", label: 'Yes' },
      { option: "no", label: 'No' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Public transport access',
      group: 'primary',
    },
    showConfig: {
      label: 'Can your home be accessed by public transport?',
      isDetail: true,
    },
    saveConfig: {
      label: 'Public transport access',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'propertyType',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "house", label: 'House' },
      { option: "apartment", label: 'Apartment' },
      { option: "townHouse/terracedHouse", label: 'Town house / Terraced house' },
      { option: "largeEstate", label: 'Large estate' },
      { option: "farm", label: 'Farm' },
      { option: "houseboat", label: 'Houseboat' },
      { option: "mobilehome/caravan", label: 'Mobile home / Caravan' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Property type',
      group: 'primary',
    },
    showConfig: {
      label: 'Property type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Property type',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'restrictedMobility',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "yes", label: 'Yes' },
      { option: "no", label: 'No' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Restricted mobility',
      group: 'primary',
    },
    showConfig: {
      label: 'Is your home accessible for those with restricted mobility?',
      isDetail: true,
    },
    saveConfig: {
      label: 'Restricted mobility',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'sittersOwnPets',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "yes", label: 'Yes' },
      { option: "no", label: 'No' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Sitters own pets',
      group: 'primary',
    },
    showConfig: {
      label: 'Is your sit suitable for sitters bringing their own pets?',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sitters own pets',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'sittersWorkFromYourHome',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "yes", label: 'Yes' },
      { option: "no", label: 'No' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Sitters work from your home',
      group: 'primary',
    },
    showConfig: {
      label: 'Is your sit suitable for sitters to work online? i.e. good Wi-Fi',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sitters work from your home',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'sitterFavourites',
    scope: 'public',
    includeForListingTypes: ['house-sit-booking'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "wi-Fi", label: 'Wi-Fi' },
      { option: "easyAccesToShops", label: 'Easy access to shops' },
      { option: "easyAccessToDoctor", label: 'Easy access to Doctor' },
      { option: "easyAccessToBank/cashMachine", label: 'Easy access to bank / cash machine' },
      { option: "accessToGarden", label: 'Access to garden' },
      { option: "swimmingPoolAtHome", label: 'Swimming pool at home' },
      { option: "washerAnd/Dryer", label: 'Washer and / dryer' },
      { option: "offStreetParking", label: 'Off-street parking' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Sitter favourites',
      group: 'primary',
    },
    showConfig: {
      label: 'Sitter favourites',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sitter favourites',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },


  {
    key: 'gender',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'enum',
    enumOptions: genderTypes,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Gender',
      group: 'primary',
    },
    showConfig: {
      label: 'Gender',
      isDetail: true,
    },
    saveConfig: {
      label: 'Gender',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one option.',
    },
  },
  {
    key: 'age',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'enum',
    enumOptions: ageTypes,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Age',
      group: 'primary',
    },
    showConfig: {
      label: 'Age',
      isDetail: true,
    },
    saveConfig: {
      label: 'Age',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select one option.',
    },
  },
  {
    key: 'desiredLocations',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'multi-enum',
    enumOptions: housesitCountries,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Desired Locations',
      group: 'primary',
    },
    showConfig: {
      label: 'Desired Locations',
      isDetail: true,
    },
    saveConfig: {
      label: 'Desired Locations',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one option.',
    },
  },

  {
    key: 'locationCharacteristics',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'beach', label: 'Beach' },
      { option: 'city', label: 'City or Town' },
      { option: 'countryside', label: 'Countryside' },
      { option: 'mountains', label: 'Mountains' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Product group',
      group: 'primary',
    },
    showConfig: {
      label: 'What kind of places do you like to visit when you housesit',
      isDetail: true,
    },
    saveConfig: {
      label: 'Location characteristics',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'publicTransportAccess',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'multi-enum',
    enumOptions: [
      { option: "yes", label: 'Yes' },
      { option: "no", label: 'No' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Public transport access',
      group: 'primary',
    },
    showConfig: {
      label: 'Need easy access to public transport',
      isDetail: true,
    },
    saveConfig: {
      label: 'Public transport access',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'propertyMaintananceExperience',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'multi-enum',
    enumOptions: propertyExperienceTypes,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Property maintanance experience',
      group: 'primary',
    },
    showConfig: {
      label: 'Property maintanance experience',
      isDetail: true,
    },
    saveConfig: {
      label: 'Property maintanance experience',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },
  {
    key: 'availableForCommunication',
    scope: 'public',
    includeForListingTypes: ['house-sitter'],
    schemaType: 'multi-enum',
    enumOptions: availableForCommunication,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Video call to get to know the owners',
      group: 'primary',
    },
    showConfig: {
      label: 'Video call to get to know the owners',
      isDetail: true,
    },
    saveConfig: {
      label: 'Video call to get to know the owners',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select at least one characteristic.',
    },
  },



];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  {
    listingType: 'house-sit-booking',
    label: 'House sit  booking',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
    defaultListingFields: {
      location: true,
      payoutDetails: false,
    },
  },
  {
    listingType: 'house',
    label: 'House details',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
    defaultListingFields: {
      location: true,
      payoutDetails: false,
    },
  },
  {
    listingType: 'house-sitter',
    label: 'House sitter',
    transactionType: {
      process: 'default-inquiry',
      alias: 'default-inquiry/release-1',
      unitType: 'inquiry',
    },
    defaultListingFields: {
      price: false,
      location: true,
    },
  },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
